/**
 * i18next Configuration.
 *
 * Sets up internationalization with:
 * - HTTP backend for loading translation files from /public/locales
 * - Browser language detection
 * - Namespace-based organization for lazy loading
 * - RTL support for Hebrew and other RTL languages
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  SUPPORTED_UI_LANGUAGE_CODES,
  DEFAULT_UI_LANGUAGE,
  isRTLLanguage,
  toBaseLanguage,
} from '../constants/uiLanguages';

// Available namespaces - add new ones here as you create translation files
export const NAMESPACES = {
  common: 'common',
  referenceData: 'referenceData',
  auth: 'auth',
  class: 'class',
  course: 'course',
  library: 'library',
  lesson: 'lesson',
  activity: 'activity',
  elements: 'elements',
  liveChat: 'liveChat',
  scoring: 'scoring',
  reports: 'reports',
  dashboard: 'dashboard',
  assignment: 'assignment',
  credits: 'credits',
  subscription: 'subscription',
  organization: 'organization',
  invitation: 'invitation',
  documentBlocks: 'documentBlocks',
  curriculum: 'curriculum',
  knowledgeCenter: 'knowledgeCenter',
  studio: 'studio',
  platformAssistant: 'platformAssistant',
  access: 'access',
  rubric: 'rubric',
  attempt: 'attempt',
  bugReports: 'bugReports',
  learnerProfile: 'learnerProfile',
  learnerKnowledgeGraph: 'learnerKnowledgeGraph',
  teacherAssistant: 'teacherAssistant',
  export: 'export',
  website: 'website',
  workFeedback: 'workFeedback',
  planner: 'planner',
  aiTemplates: 'aiTemplates',
  myProgress: 'myProgress',
} as const;

// Default namespace used when no namespace is specified
export const DEFAULT_NS = NAMESPACES.common;

// Namespaces to load on initial page load (keep minimal for performance)
export const PRELOAD_NAMESPACES = [NAMESPACES.common, NAMESPACES.referenceData];

/**
 * Update document direction based on current language.
 * Called on language change to handle RTL/LTR switching.
 */
const updateDocumentDirection = (lng: string) => {
  const dir = isRTLLanguage(lng) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lng);
};

i18n
  // Load translations from /public/locales/{lng}/{ns}.json
  .use(HttpBackend)
  // Detect user language from browser settings
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  .init({
    // Supported languages (from centralized config)
    supportedLngs: SUPPORTED_UI_LANGUAGE_CODES,

    // Fallback language when translation is missing
    fallbackLng: DEFAULT_UI_LANGUAGE,

    // Resolve region tags (e.g. "he-IL") to their base language ("he")
    load: 'languageOnly',

    // Default namespace
    defaultNS: DEFAULT_NS,

    // Namespaces to preload
    ns: PRELOAD_NAMESPACES,

    // Debug mode - disabled to prevent console spam from missing keys
    // Set to true temporarily when debugging i18n issues
    debug: false,

    // Interpolation settings
    interpolation: {
      // React already handles XSS protection
      escapeValue: false,
    },

    // Backend configuration for loading translation files
    backend: {
      // Path to translation files
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // Always revalidate so updated translations are never served stale
      requestOptions: {
        cache: 'no-cache',
      },
    },

    // Language detection configuration
    detection: {
      // Explicit user preference wins; browser language decides only for first visits
      order: ['localStorage', 'navigator'],
      // Cache language preference in localStorage
      caches: ['localStorage'],
      // Key used in localStorage
      lookupLocalStorage: 'i18nextLng',
      // i18next would keep "he-IL" as the language; the supported codes are base languages
      convertDetectedLanguage: toBaseLanguage,
    },

    // React-specific settings
    react: {
      // Wait for translations to load before rendering (prevents flash)
      useSuspense: true,
    },
  });

// Set initial document direction
updateDocumentDirection(i18n.language || DEFAULT_UI_LANGUAGE);

// Update direction on language change
i18n.on('languageChanged', updateDocumentDirection);

export default i18n;
